import {Mutation, Query} from "./graphql/queries"
import {Query as ReactQuery} from "react-apollo"
import {FetchPolicy} from "apollo-client"
import * as React from "react"
import {ReactElement} from "react"
import {assertNonNull, mixed} from "./tsutil"
import {ErrorComponent} from "./ErrorBoundary"
import {apollo} from "./App"

function isEmptyData(data: object): boolean {
  const proto = Object.getPrototypeOf(data)
  return (
    Object.getOwnPropertyNames(data).length === 0 &&
    // Sometimes the data object has a single symbol on it with the value "ROOT_QUERY" even
    // if it has no named properties. We still need to consider such an object as empty.
    // Object.getOwnPropertySymbols(data).length === 0 &&

    // Old versions of Apollo React provided an instance of Object (i.e. its prototype was
    // Object.prototype). New versions (since 2.1.4 at least) provide an object without a
    // prototype (its prototype is null).
    (proto === null || proto === Object.prototype)
  )
}

interface GraphQLQueryRenderProps<Out> {
  data: Out | null
  loading: boolean
  refetch: () => Promise<Out>
}

interface GraphQLQueryProps<In, Out> {
  query: Query<In, Out>
  variables: In
  fetchPolicy?: FetchPolicy
  // Duration in milliseconds over which to poll the server. Defaults to 0 which disables polling.
  pollInterval?: number
  skip?: boolean
  render: (props: GraphQLQueryRenderProps<Out>) => ReactElement<mixed> | null
}

function GraphQLQuery<In, Out>({
  query,
  variables,
  fetchPolicy = "cache-first",
  pollInterval = 0,
  render,
  skip = false,
}: GraphQLQueryProps<In, Out>) {
  return (
    <ReactQuery
      client={assertNonNull(apollo)}
      query={query.document}
      variables={variables}
      fetchPolicy={fetchPolicy}
      pollInterval={pollInterval}
      skip={skip}
      children={({loading, error, data, refetch}) => {
        if (error) {
          // Note that Apollo is dumb and ignores both the response code and the Content-Type from the
          // HTTP server, so when our server reports "HTTP 500: Error please try again later" Apollo
          // tries to parse the body as JSON and the resulting SyntaxError will be your "network
          // error". :\
          // Note: This is correct as of apollo-client 2.3.2
          // Handle the error here instead of throwing it to the nearest error boundary so that
          // it is localised as much as possible and the user is more likely to be able to continue
          // what they were doing once our server recovers or they can at least copy stuff out of
          // the page.
          // TODO We could expose a "Retry" button here that would call refetch().
          const realError = error.networkError || error
          console.error(realError)
          return <ErrorComponent error={realError} />
        }

        return render({
          data: !data || isEmptyData(data) ? null : data,
          // See https://github.com/apollographql/react-apollo/issues/1869 re loading while skip.
          loading: skip ? false : loading,
          async refetch() {
            // refetch() actually merges the variables onto the existing set of
            // variables, so it is safe to pass nothing. See:
            // tslint:disable-next-line:max-line-length
            // https://github.com/apollographql/react-apollo/blob/b6b5bff3092ecd139bfb88c15f239dd7c4837b9b/src/graphql.tsx#L576
            // tslint:disable-next-line:max-line-length
            // https://github.com/apollographql/react-apollo/blob/b6b5bff3092ecd139bfb88c15f239dd7c4837b9b/src/graphql.tsx#L162
            // tslint:disable-next-line:max-line-length
            // https://github.com/apollographql/apollo-client/blob/aee073ee223256b2a1977a10ce819419795d3f63/packages/apollo-client/src/core/ObservableQuery.ts#L258
            return (await refetch()).data
          },
        })
      }}
    />
  )
}

export interface QueryWithVars<T> {
  query: Query<T, {}>
  variables: T
}

// A function accepting a function, to achieve an existential type (T)
// This way refetchQueries can have multiple heterogeneous (query, variables) pairs but each
// set of variables has to match the query its paired with.
export type QueryWithVarsNoT = <R>(go: <T>(x: QueryWithVars<T>) => R) => R

export function makeQueryWithVarsNoT<T>(p: QueryWithVars<T>): QueryWithVarsNoT {
  return (go) => go(p)
}

export const GraphQL = {
  Query: GraphQLQuery,

  async query<A, T>(
    query: Query<A, T>,
    variables: A,
    {
      noCache = false,
    }: {
      noCache?: boolean
    } = {},
  ): Promise<T> {
    const ret = await apollo.query<T>({
      query: query.document,
      fetchPolicy: noCache ? "network-only" : "cache-first",
      variables,
    })
    return ret.data
  },

  async mutate<A, T>(
    query: Mutation<A, T>,
    variables: A,
    {
      refetchQueries = [],
    }: {
      refetchQueries?: Array<QueryWithVarsNoT>
    } = {},
  ): Promise<T> {
    const ret = await apollo.mutate({
      mutation: query.document,
      variables,
      // We need to map refetchQueries to remove the existential type
      // and get the DocumentNode out of the Query
      refetchQueries: refetchQueries.map((x) =>
        x(({query, variables}) => ({
          query: query.document,
          variables,
        })),
      ),
    })
    return ret.data as T
  },
}



// WEBPACK FOOTER //
// ./src/graphql.tsx